/**
 * Compliance service for PDT rule monitoring (T145).
 */
import { api } from './api';

/**
 * PDT status response from the API.
 */
export interface PDTStatus {
  dayTradesCount: number;
  dayTradesLimit: number;
  remaining: number;
  isAtLimit: boolean;
  windowStart: string;
  windowEnd: string;
}

/**
 * Day trade record from the API.
 */
export interface DayTrade {
  id: string;
  symbol: string;
  tradeDate: string;
  quantity: number;
  buyPrice: number;
  sellPrice: number;
  profitLoss: number;
  buyOrderId: string;
  sellOrderId: string;
  createdAt: string;
}

/**
 * Day trades list response from the API.
 */
export interface DayTradeListResponse {
  items: DayTrade[];
  total: number;
}

/**
 * Compliance service for interacting with the compliance API endpoints.
 */
export const complianceService = {
  /**
   * Get current PDT status.
   *
   * Returns the current day trade count, remaining trades, and whether
   * the account is at the PDT limit.
   */
  async getPDTStatus(): Promise<PDTStatus> {
    const response = await api.get<{
      day_trades_count: number;
      day_trades_limit: number;
      remaining: number;
      is_at_limit: boolean;
      window_start: string;
      window_end: string;
    }>('/compliance/pdt-status');

    // Transform snake_case to camelCase
    return {
      dayTradesCount: response.day_trades_count,
      dayTradesLimit: response.day_trades_limit,
      remaining: response.remaining,
      isAtLimit: response.is_at_limit,
      windowStart: response.window_start,
      windowEnd: response.window_end,
    };
  },

  /**
   * Get list of recent day trades.
   *
   * @param limit - Maximum number of records to return (default 20, max 100).
   */
  async getDayTrades(limit: number = 20): Promise<DayTradeListResponse> {
    const response = await api.get<{
      items: Array<{
        id: string;
        symbol: string;
        trade_date: string;
        quantity: number;
        buy_price: number;
        sell_price: number;
        profit_loss: number;
        buy_order_id: string;
        sell_order_id: string;
        created_at: string;
      }>;
      total: number;
    }>(`/compliance/day-trades?limit=${limit}`);

    // Transform snake_case to camelCase
    return {
      items: response.items.map((item) => ({
        id: item.id,
        symbol: item.symbol,
        tradeDate: item.trade_date,
        quantity: item.quantity,
        buyPrice: item.buy_price,
        sellPrice: item.sell_price,
        profitLoss: item.profit_loss,
        buyOrderId: item.buy_order_id,
        sellOrderId: item.sell_order_id,
        createdAt: item.created_at,
      })),
      total: response.total,
    };
  },
};

export default complianceService;
